import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import ChevronRightIcon from '../../../../assets/icons/chevron-right.svg?react';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive.js';
import GiftCard from '../../../../components/GiftCard/index.jsx';
import { useSolana } from '../../../../hooks/useSolana.js';
import ConfirmationSkeleton from './skeleton.jsx';
import { selectTokenByNetworkAndAddress } from '../../../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../../../redux/slices/walletSlice';
import {
  EVENT_GIFT_FLOW_APPLE_PAY_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTED,
  EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTOR_OPENED,
  sendEvent,
} from '../../../../common/analytics.js';
import {
  formatUSD,
  formatUSDEnsure2Digits,
} from '../../../../common/currency.js';
import { formatDate } from '../../../../common/date.js';
import ScrollRestore from '../../../../components/ScrollRestore/index.tsx';
import { useGiftForm } from '../../../../components/GiftFormContext/index.jsx';
import Button from '../../../../components/Button/index.tsx';
import SendingStatus from '../../../../views/Gifts/SendingStatus/index.jsx';
import { openModal, closeModal } from '../../../../redux/slices/modals.js';
import useApplePay, {
  BALANCE_TOLERANCE,
  calculateApplePayFeeAndTotal,
} from '../../../../hooks/useApplePay.js';
import { useGiftAmountCalculations } from '../../../../hooks/useGiftAmountCalculations';
import useApplePayCheckout from '../../../../hooks/useApplePayCheckout.js';
import { ApplePayWidget } from '../../../../components/ApplePayWidget';
import ApplePayButton from '../../../../components/ApplePayButton';
import ApplePayMark from '../../../../assets/icons/applepay.svg?react';
import PhoneVerification from './components/PhoneVerification';
import { useGetCurrentUserProfileQuery } from '../../../../redux/api/authorized/auth';
import { compressBase64Image } from '../../../../common/images';
import { useCreateCustomGiftCardDesignMutation } from '../../../../redux/api/authorized/customGiftCardDesigns.js';
import ContentLoader from 'react-content-loader';

const GiftCardContainer = styled.div`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  flex: 1;

  .stripe-onramp-wrapper {
    margin: 0;
    padding: 0;
  }
`;

const Line = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const Message = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 4px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const ContentRow = styled.div`
  display: flex;
  width: 100%;
  gap: 48px;
  color: ${({ theme }) => theme.text.regular};
  flex-direction: column;
`;

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  flex-direction: row;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 20px;
  }
`;

const GiftCardWrapper = styled.div`
  width: 100%;
  max-width: 50%;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
    width: 100%;
  }
`;

const SummaryWrapper = styled.div`
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const PaymentSelectorContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  cursor: pointer;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
`;

const TokenImage = styled.img`
  width: 24px;
  height: 24px;
`;

const PaymentTokenInfoText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
`;

const SmallLine = () => {
  const theme = useTheme();
  return (
    <ContentLoader
      speed={2}
      width={80}
      height={20}
      viewBox='0 0 80 20'
      backgroundColor={`${theme.background.secondary}`}
      foregroundColor={`${theme.special.skeleton}`}
      style={{ width: '100%', maxWidth: '80px' }}
    >
      <rect x='0' y='0' rx='4' ry='4' width='80' height='20' />
    </ContentLoader>
  );
};

const Confirmation = ({ isModal, currentStep }) => {
  const theme = useTheme();
  const { applePayEnabled } = useApplePay();
  const { data: currentUser } = useGetCurrentUserProfileQuery();
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);

  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();

  const [pendingApplePayUsdAmount, setPendingApplePayUsdAmount] =
    useState(null);
  const [waitingForApplePayBalance, setWaitingForApplePayBalance] =
    useState(false);

  const dispatch = useDispatch();

  const {
    feedback,
    network,
    image,
    targetTokenAddress,
    targetTokenAmount,
    delivery,
    setSelectedPaymentToken,
    email,
    message,
    setFeedback,
    sendingStatus,
    setSendingStatus,
    claimUrl,
    amountSpecifiedInUsd,
    usdAmount,
    selectedPaymentToken,
    selectedPaymentMethodType,
    setSelectedPaymentMethodType,
    senderName,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    country,
    setValue,
    trigger,
    submitGift,
    targetToken,
    usdcToken,
    isUserEligible,
    showFreeGift,
    isLoadingGift,
    sendOnDate,
  } = useGiftForm();

  const {
    formatedTokenAmountDisplay,
    formatedAmountUSD,
    feeCalculations,
    giftFeesResponse,
    tokensWithEnoughBalance,
    tokensWithUSDBalance,
    isLoadingGiftFees,
    bonusUsdAmount,
  } = useGiftAmountCalculations({
    network,
    targetTokenAddress,
    targetTokenAmount,
    usdAmount: amountSpecifiedInUsd ? usdAmount : undefined,
    amountSpecifiedInUsd: amountSpecifiedInUsd,
    delivery,
    selectedPaymentMethodType,
  });

  const usdcTokenBalance = useSelector((state) =>
    selectTokenBalance('solana', usdcToken?.address)(state),
  );

  const [fullMessage, setFullMessage] = useState(false);

  const [
    userSelectedPaymentTokenUsdcBalance,
    setUserSelectedPaymentTokenUsdcBalance,
  ] = useState(0);

  const userSelectedPaymentToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(
      state,
      network,
      selectedPaymentToken?.address,
    ),
  );

  // Save local data URL image to backend when user reaches confirmation (they're logged in)
  useEffect(() => {
    const saveLocalImageIfNeeded = async () => {
      if (!image || !image.startsWith('data:')) {
        // Not a data URL, nothing to save
        return;
      }

      try {
        const compressedImage = await compressBase64Image(image);
        const response = await createCustomGiftCardDesign({
          image: compressedImage,
          type: delivery === 'mail' ? 'physical' : 'digital',
        });

        if (response.data?.imageUrl) {
          setValue('image', response.data.imageUrl, { shouldValidate: true });
          await trigger('image');
        }
      } catch (error) {
        console.error('Failed to save local image:', error);
      }
    };

    saveLocalImageIfNeeded();
  }, [image, delivery, createCustomGiftCardDesign, setValue, trigger]); // Run when image or delivery changes

  useEffect(() => {
    if (userSelectedPaymentToken && tokensWithEnoughBalance) {
      const usToken = tokensWithEnoughBalance.find(
        (token) => token.address === userSelectedPaymentToken?.address,
      );
      if (usToken) {
        setUserSelectedPaymentTokenUsdcBalance(usToken.usdValue);
      } else {
        setUserSelectedPaymentTokenUsdcBalance(0);
      }
    }
  }, [userSelectedPaymentToken, tokensWithEnoughBalance]);

  const {
    startCheckout: startApplePayCheckout,
    widgetProps: applePayWidgetProps,
    isStarting: applePayStarting,
    isProcessingPayment: applePayProcessing,
    reset: resetApplePayCheckout,
  } = useApplePayCheckout();

  // Simple polling: refresh balances every 3 seconds
  const { refreshTokenBalances } = useSolana();

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenBalances({ force: true });
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshTokenBalances]);

  // Validation: Check if currently selected token still has enough balance
  // Runs when tokensWithEnoughBalance or selectedPaymentToken changes
  // Clears selection if the selected token no longer has sufficient balance
  useEffect(() => {
    // Skip if data not ready or no payment method selected
    if (
      tokensWithEnoughBalance === null ||
      !selectedPaymentMethodType ||
      selectedPaymentMethodType !== 'token'
    ) {
      return;
    }

    // Skip if Apple Pay is in progress (don't interfere)
    if (
      selectedPaymentMethodType === 'applePay' &&
      (waitingForApplePayBalance || applePayProcessing || applePayStarting)
    ) {
      return;
    }

    // Check if selected token still has enough balance
    if (selectedPaymentToken && tokensWithEnoughBalance.length > 0) {
      const selectedTokenHasBalance = tokensWithEnoughBalance.some(
        (token) => token.address === selectedPaymentToken.address,
      );

      // If token no longer has enough balance, clear the selection
      if (!selectedTokenHasBalance) {
        setSelectedPaymentMethodType(null);
        setSelectedPaymentToken(usdcToken); // Reset to USDC for calculations
      }
    } else if (tokensWithEnoughBalance.length === 0 && selectedPaymentToken) {
      // No tokens have enough balance - clear token selection
      setSelectedPaymentMethodType(null);
      setSelectedPaymentToken(usdcToken); // Reset to USDC for calculations
    }
  }, [
    tokensWithEnoughBalance,
    selectedPaymentToken,
    selectedPaymentMethodType,
    waitingForApplePayBalance,
    applePayProcessing,
    applePayStarting,
    usdcToken,
    setSelectedPaymentToken,
    setSelectedPaymentMethodType,
  ]);

  // Auto-selection: Automatically select payment method when data is ready
  // Runs when tokensWithEnoughBalance changes and no payment method is selected
  // Only auto-selects if user hasn't made a selection yet
  useEffect(() => {
    // Skip if required data not ready
    if (!targetToken || !usdcToken || !usdAmount) {
      return;
    }

    // Wait for balance calculations to complete
    if (tokensWithEnoughBalance === null) {
      return;
    }

    // Don't override user's Apple Pay selection if in progress
    if (
      selectedPaymentMethodType === 'applePay' &&
      (waitingForApplePayBalance || applePayProcessing || applePayStarting)
    ) {
      return;
    }

    // Only auto-select if no payment method is currently selected
    if (selectedPaymentMethodType) {
      return;
    }

    if (tokensWithEnoughBalance.length > 0) {
      // Prefer target token if it has enough balance (no swap needed)
      const targetTokenWithBalance = tokensWithEnoughBalance.find(
        (token) => token.address === targetToken.address,
      );

      if (targetTokenWithBalance) {
        // Target token has enough balance - use it (no swap needed)
        setSelectedPaymentToken(targetTokenWithBalance);
        setSelectedPaymentMethodType('token');
      } else {
        // Target token doesn't have enough balance - select token with highest balance
        const tokenWithMostBalance = tokensWithEnoughBalance.reduce(
          (max, token) => {
            return (token.usdValue || 0) > (max.usdValue || 0) ? token : max;
          },
          tokensWithEnoughBalance[0],
        );

        setSelectedPaymentToken(tokenWithMostBalance);
        setSelectedPaymentMethodType('token');
      }
    } else {
      // No tokens have enough balance - default to USDC for calculations
      // Don't set payment method type (user must select via PaymentSuggestion)
      setSelectedPaymentToken(usdcToken);

      // Optionally default to Apple Pay if enabled and total amount (gift + shipping + Apple Pay fee) meets minimum
      const giftAmount = parseFloat(formatedAmountUSD || 0);
      const shippingFees = parseFloat(
        giftFeesResponse?.totalFees?.usdFees || 0,
      );
      const baseAmount = giftAmount + shippingFees;
      // Calculate Apple Pay fee for the check using shared calculation
      const { total: totalAmount } = calculateApplePayFeeAndTotal(baseAmount);
      const isWeb = !Capacitor.isNativePlatform();
      if ((applePayEnabled || isWeb) && totalAmount >= 5) {
        setSelectedPaymentMethodType('applePay');
      }
    }
  }, [
    tokensWithEnoughBalance,
    targetToken,
    usdcToken,
    usdAmount,
    applePayEnabled,
    selectedPaymentMethodType,
    waitingForApplePayBalance,
    applePayProcessing,
    applePayStarting,
    setSelectedPaymentToken,
    setSelectedPaymentMethodType,
    formatedAmountUSD,
    giftFeesResponse,
  ]);

  // Fee calculations and balance filtering are now handled in useGiftConfirmationCalculations hook

  const userSelectedTokenBalance = useSelector((state) =>
    selectTokenBalance(network, selectedPaymentToken?.address)(state),
  );

  // TODO: Get estimated delivery date from backend
  const estimatedDeliveryDate = '7-10 business days';

  // Gift creation is now centralized in GiftFormContext
  // Use submitGift for full flow, or createGift for just creating the gift
  const createGiftHandler = async () => {
    try {
      await submitGift();
    } catch (error) {
      // Error handling is done in submitGift, but we can add component-specific handling here if needed
      console.error('Error in createGiftHandler:', error);
    }
  };

  useEffect(() => {
    return () => {
      resetApplePayCheckout();
      setPendingApplePayUsdAmount(null);
      setWaitingForApplePayBalance(false);
    };
  }, [resetApplePayCheckout]);

  // Reset Apple Pay state when payment method changes away from Apple Pay
  useEffect(() => {
    if (selectedPaymentMethodType !== 'applePay') {
      if (
        applePayWidgetProps ||
        pendingApplePayUsdAmount ||
        waitingForApplePayBalance
      ) {
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      }
    }
  }, [
    selectedPaymentMethodType,
    applePayWidgetProps,
    pendingApplePayUsdAmount,
    waitingForApplePayBalance,
    resetApplePayCheckout,
  ]);

  const startApplePayFlow = useCallback(() => {
    const resolvedUsdAmount = amountSpecifiedInUsd
      ? parseFloat(usdAmount ?? '0')
      : parseFloat(formatedAmountUSD ?? '0');

    if (!resolvedUsdAmount || Number.isNaN(resolvedUsdAmount)) {
      setFeedback('Unable to determine amount for Apple Pay checkout.');
      return;
    }

    // Recalculate amount fresh to ensure we have the latest fees
    // This prevents using stale feeCalculations if it hasn't updated yet
    const giftAmount = parseFloat(formatedAmountUSD || 0);
    const shippingFees = parseFloat(giftFeesResponse?.totalFees?.usdFees || 0);
    const applePayBaseAmount = giftAmount + shippingFees;

    // Validate that shipping fees are available for physical delivery
    if (delivery === 'mail' && shippingFees <= 0) {
      setFeedback('Unable to calculate shipping fees. Please try again.');
      return;
    }

    startApplePayCheckout(applePayBaseAmount, {
      onPending: () => {
        setFeedback(null);
      },
      onSuccess: () => {
        setFeedback('Waiting for Apple Pay funds to settle...');
        setSendingStatus('sending');
        // Store the total amount (gift + shipping) that we need to wait for
        setPendingApplePayUsdAmount(applePayBaseAmount);
        setWaitingForApplePayBalance(true);
      },
      onError: (err) => {
        if (err) {
          setFeedback('Unable to start Apple Pay. Please try again.');
        } else {
          setFeedback('Apple Pay payment failed. Please try again.');
        }
        setSendingStatus('idle');
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onAlert: (title, message, type) => {
        if (type === 'error') {
          setFeedback(
            `Apple Pay error: ${title || message || 'Payment failed'}`,
          );
          resetApplePayCheckout();
          setPendingApplePayUsdAmount(null);
          setWaitingForApplePayBalance(false);
          setSendingStatus('idle');
        } else if (type === 'info') {
          setFeedback(message || title || null);
        }
      },
    });
  }, [
    formatedAmountUSD,
    amountSpecifiedInUsd,
    resetApplePayCheckout,
    setFeedback,
    setSendingStatus,
    setPendingApplePayUsdAmount,
    setWaitingForApplePayBalance,
    startApplePayCheckout,
    usdAmount,
    feeCalculations,
    giftFeesResponse,
    delivery,
  ]);

  const handleApplePayPress = useCallback(() => {
    // Check if we're on web (not in Capacitor/iOS app)
    // If on web, show modal to download app or pay with crypto
    sendEvent(EVENT_GIFT_FLOW_APPLE_PAY_BUTTON_CLICKED, {
      amount: feeCalculations.totalAmount,
      is_web: !Capacitor.isNativePlatform(),
    });

    // If on web and Apple Pay is not enabled (e.g. feature flag off), show download modal
    const isWeb = !Capacitor.isNativePlatform();
    if (isWeb) {
      // Check if total amount (gift + shipping fees + Apple Pay fee) meets $5 minimum
      const totalAmount = feeCalculations.totalAmount;

      if (totalAmount < 5) {
        setFeedback('Apple Pay requires a minimum of $5');
        return;
      }

      dispatch(
        openModal({
          name: 'applePayDownload',
          props: {
            onPayWithCrypto: () => {
              // Open payment suggestion modal to select crypto payment
              dispatch(
                openModal({
                  name: 'paymentSuggestion',
                  props: {
                    amount:
                      feeCalculations.giftAmount + feeCalculations.shippingFees,
                    targetToken: targetToken,
                    onCancel: () => {
                      dispatch(closeModal());
                    },
                    onPaymentMethodSelected: (paymentData) => {
                      sendEvent(EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTED, {
                        paymentMethod: paymentData,
                      });
                      if (paymentData.type === 'token') {
                        setSelectedPaymentMethodType('token');
                        if (paymentData.token) {
                          setSelectedPaymentToken(paymentData.token);
                        }
                      }
                      dispatch(closeModal());
                    },
                  },
                }),
              );
            },
          },
        }),
      );
      return;
    }

    // On native platform, check if Apple Pay is enabled and not processing
    if (!applePayEnabled || applePayStarting || applePayProcessing) {
      return;
    }

    // Check if total amount (gift + shipping fees + Apple Pay fee) meets $5 minimum
    const totalAmount = feeCalculations.totalAmount;

    if (totalAmount < 5) {
      setFeedback('Apple Pay requires a minimum of $5');
      return;
    }

    // Check if phone is verified - if not, show inline phone verification
    if (!currentUser?.phoneNumber) {
      setShowPhoneVerification(true);
      return;
    }

    // Phone is verified, proceed with Apple Pay
    startApplePayFlow();
  }, [
    applePayEnabled,
    applePayProcessing,
    applePayStarting,
    currentUser?.phoneNumber,
    startApplePayFlow,
    feeCalculations,
    setFeedback,
    dispatch,
    targetToken,
    setSelectedPaymentMethodType,
    setSelectedPaymentToken,
  ]);

  const handlePhoneVerified = useCallback(() => {
    setShowPhoneVerification(false);
    // After phone verification, proceed with Apple Pay
    startApplePayFlow();
  }, [startApplePayFlow]);

  const handlePhoneVerificationBack = useCallback(() => {
    setShowPhoneVerification(false);
  }, []);

  // Check if Apple Pay balance becomes sufficient (polling happens separately every 3 seconds)
  useEffect(() => {
    if (!waitingForApplePayBalance || pendingApplePayUsdAmount == null) {
      return;
    }

    const numericBalance = parseFloat(usdcTokenBalance ?? '0');
    if (!Number.isNaN(numericBalance)) {
      // Use tolerance to account for minor USDC fluctuations and floating point precision
      const requiredBalance = Math.max(
        0,
        pendingApplePayUsdAmount - BALANCE_TOLERANCE,
      );
      if (numericBalance >= requiredBalance) {
        setWaitingForApplePayBalance(false);
        setPendingApplePayUsdAmount(null);
        // Show clear feedback that payment is processing, not just received
        setFeedback('Payment processing...');
        if (
          selectedPaymentMethodType === 'applePay' &&
          usdcToken &&
          (!selectedPaymentToken ||
            selectedPaymentToken.address !== usdcToken.address)
        ) {
          setSelectedPaymentToken(usdcToken);
        }
        setTimeout(() => {
          createGiftHandler();
        }, 0);
        resetApplePayCheckout();
      }
    }
  }, [
    waitingForApplePayBalance,
    pendingApplePayUsdAmount,
    usdcTokenBalance,
    usdcToken,
    selectedPaymentMethodType,
    selectedPaymentToken,
    createGiftHandler,
    resetApplePayCheckout,
    setFeedback,
    setSelectedPaymentToken,
  ]);

  // Amount calculations are now handled in useGiftConfirmationCalculations hook

  if (!targetToken) {
    return <ConfirmationSkeleton />;
  }

  if (sendingStatus === 'sending' || sendingStatus === 'success') {
    return (
      <>
        <SendingStatus
          giftUrl={claimUrl}
          email={email}
          token={targetToken}
          cardImage={image}
          isSuccess={sendingStatus === 'success'}
          usdAmount={amountSpecifiedInUsd ? usdAmount : undefined}
          tokenAmount={
            amountSpecifiedInUsd
              ? formatedTokenAmountDisplay
              : formatedTokenAmountDisplay || targetTokenAmount
          }
          estimatedDeliveryDate={estimatedDeliveryDate}
          isPhysical={delivery === 'mail'}
          sendOnDate={sendOnDate}
        />
      </>
    );
  }

  // Show phone verification inline if needed
  if (showPhoneVerification) {
    return (
      <>
        <ScrollRestore />
        <DemoContainer>
          <PhoneVerification
            onVerified={handlePhoneVerified}
            onBack={handlePhoneVerificationBack}
          />
        </DemoContainer>
      </>
    );
  }

  return (
    <>
      <ScrollRestore />
      {/* Only render Apple Pay widget when Apple Pay is selected and processing */}
      {selectedPaymentMethodType === 'applePay' && applePayWidgetProps && (
        <ApplePayWidget {...applePayWidgetProps} />
      )}
      <DemoContainer>
        <ContentRow>
          <TwoColumnContainer>
            <GiftCardWrapper>
              <GiftCardContainer>
                <GiftCard
                  usdAmount={amountSpecifiedInUsd ? usdAmount : undefined}
                  tokenAmount={
                    !amountSpecifiedInUsd
                      ? formatedTokenAmountDisplay || targetTokenAmount
                      : formatedTokenAmountDisplay
                  }
                  cardImage={image}
                  token={targetToken}
                  isPhysical={delivery === 'mail'}
                  giftCardText={message}
                  senderName={senderName}
                  displayImageSelector={false}
                  isFreeGift={showFreeGift}
                />
              </GiftCardContainer>
            </GiftCardWrapper>
            <SummaryWrapper>
              <FormContainer>
                <TitleRow>
                  <SummaryTitle>{targetToken.symbol} Gift Card</SummaryTitle>
                  {feeCalculations.totalAmount && !isLoadingGift ? (
                    <SummaryTitle>
                      {formatUSD(feeCalculations.totalAmount)}
                    </SummaryTitle>
                  ) : (
                    <SmallLine />
                  )}
                </TitleRow>
                <SummaryRow style={{ justifyContent: 'flex-start', gap: 8 }}>
                  <SummaryDescription>
                    <span style={{ color: `${theme.text.secondary}` }}>
                      Gift Amount:
                    </span>{' '}
                  </SummaryDescription>
                  <SummaryDescription>
                    {feeCalculations.giftAmount && !isLoadingGift ? (
                      bonusUsdAmount ? (
                        `${formatUSD(feeCalculations.giftAmount + bonusUsdAmount)} (${formatUSD(bonusUsdAmount)} bonus)`
                      ) : (
                        formatUSD(feeCalculations.giftAmount)
                      )
                    ) : (
                      <SmallLine />
                    )}
                  </SummaryDescription>
                </SummaryRow>
                {(feeCalculations.shippingFees > 0 ||
                  feeCalculations.usdDiscount > 0) && (
                  <>
                    <SummaryRow>
                      <SummaryDescription
                        style={{
                          textDecoration:
                            feeCalculations.usdDiscount > 0
                              ? 'line-through'
                              : 'none',
                        }}
                      >
                        <span style={{ color: `${theme.text.secondary}` }}>
                          Shipping Fees:
                        </span>{' '}
                        {formatUSD(
                          feeCalculations.usdDiscount > 0
                            ? feeCalculations.usdDiscount
                            : feeCalculations.shippingFees,
                        )}
                      </SummaryDescription>
                    </SummaryRow>
                    {feeCalculations.usdDiscount > 0 && (
                      <SummaryRow>
                        <SummaryDescription>
                          <span style={{ color: `${theme.text.success}` }}>
                            First Gift Discount!
                          </span>{' '}
                        </SummaryDescription>
                      </SummaryRow>
                    )}
                  </>
                )}
                {selectedPaymentMethodType === 'applePay' &&
                  feeCalculations.applePayFee > 0 && (
                    <SummaryRow>
                      <SummaryDescription>
                        <span style={{ color: `${theme.text.secondary}` }}>
                          Payment Fee:
                        </span>{' '}
                        {formatUSD(feeCalculations.applePayFee)}
                      </SummaryDescription>
                    </SummaryRow>
                  )}
                {delivery === 'mail' && senderName && (
                  <SummaryRow>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        From:
                      </span>{' '}
                      {senderName}
                    </SummaryDescription>
                  </SummaryRow>
                )}
                {delivery === 'email' && (
                  <SummaryRow>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        Receiver:
                      </span>{' '}
                      {email}
                    </SummaryDescription>
                  </SummaryRow>
                )}
                {sendOnDate && delivery === 'email' && (
                  <SummaryRow>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        Send on:
                      </span>{' '}
                      {formatDate(new Date(sendOnDate))}
                    </SummaryDescription>
                  </SummaryRow>
                )}
                {message && (
                  <SummaryRow style={{ flexDirection: 'column' }}>
                    <SummaryDescription>
                      <span style={{ color: `${theme.text.secondary}` }}>
                        Message
                      </span>
                    </SummaryDescription>
                    <SummaryDescription>
                      {fullMessage ? message : `${message.slice(0, 20)}...`}{' '}
                      {!fullMessage && (
                        <span
                          onClick={() => setFullMessage(true)}
                          style={{
                            color: `${theme.text.secondary}`,
                            cursor: 'pointer',
                          }}
                        >
                          See more
                        </span>
                      )}
                    </SummaryDescription>
                  </SummaryRow>
                )}
              </FormContainer>
            </SummaryWrapper>
          </TwoColumnContainer>
        </ContentRow>
        {
          <ContentRow>
            <FormContainer>
              {delivery === 'mail' && addressLine1 && (
                <SummaryRow style={{ marginTop: 20 }}>
                  <SummaryDescription>
                    <span style={{ color: `${theme.text.secondary}` }}>
                      Shipping Address:
                    </span>{' '}
                    {[
                      addressLine1,
                      addressLine2,
                      [city, state, postalCode].filter(Boolean).join(', '),
                      country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </SummaryDescription>
                </SummaryRow>
              )}
              {delivery === 'mail' && (
                <SummaryDescription
                  style={{
                    fontStyle: 'italic',
                    textAlign: 'left',
                    width: '100%',
                    marginTop: 8,
                  }}
                >
                  Estimated delivery: {estimatedDeliveryDate}
                </SummaryDescription>
              )}
            </FormContainer>
          </ContentRow>
        }
        <Line />
        <ContentRow>
          <FormContainer>
            {!(isUserEligible && showFreeGift) && (
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  marginBottom: 20,
                  width: '100%',
                  flexDirection: 'column',
                }}
              >
                <SummaryDescription>Paying with</SummaryDescription>
                <PaymentSelectorContainer
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    // Always allow opening payment selection if we have token data
                    if (
                      tokensWithUSDBalance ||
                      tokensWithEnoughBalance !== null
                    ) {
                      sendEvent(
                        EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTOR_OPENED,
                        {
                          is_free_gift: showFreeGift,
                        },
                      );
                      dispatch(
                        openModal({
                          name: 'paymentSuggestion',
                          props: {
                            amount:
                              feeCalculations.giftAmount +
                              feeCalculations.shippingFees,
                            targetToken: targetToken,
                            onCancel: () => {
                              dispatch(closeModal());
                            },
                            onPaymentMethodSelected: (paymentData) => {
                              sendEvent(
                                EVENT_GIFT_FLOW_PAYMENT_METHOD_SELECTED,
                                {
                                  paymentMethod: paymentData,
                                },
                              );
                              if (paymentData.type === 'token') {
                                setSelectedPaymentMethodType('token');
                                // If user selected a specific token, use it; otherwise USDC is already set as default
                                if (paymentData.token) {
                                  setSelectedPaymentToken(paymentData.token);
                                }
                              } else if (paymentData.type === 'applePay') {
                                setSelectedPaymentMethodType('applePay');
                                // Apple Pay always uses USDC (already set as default)
                              }
                              // Close only the payment suggestion modal
                              dispatch(closeModal());
                            },
                          },
                        }),
                      );
                    }
                  }}
                >
                  {selectedPaymentMethodType === 'applePay' ? (
                    <>
                      <ApplePayMark
                        style={{
                          width: 40,
                          height: 'auto',
                        }}
                      />
                      <PaymentTokenInfoText>Apple Pay</PaymentTokenInfoText>
                      <ChevronRightIcon width={12} height={12} />
                    </>
                  ) : selectedPaymentMethodType === 'token' &&
                    selectedPaymentToken ? (
                    <>
                      <TokenImage src={selectedPaymentToken?.logoURI} />
                      <PaymentTokenInfoText>
                        {selectedPaymentToken?.symbol} (
                        {amountSpecifiedInUsd
                          ? formatUSDEnsure2Digits(
                              userSelectedPaymentTokenUsdcBalance,
                            )
                          : userSelectedTokenBalance}
                        )
                      </PaymentTokenInfoText>
                      <ChevronRightIcon width={12} height={12} />
                    </>
                  ) : (
                    <>
                      <PaymentTokenInfoText>
                        Select payment method
                      </PaymentTokenInfoText>
                      <ChevronRightIcon width={12} height={12} />
                    </>
                  )}
                </PaymentSelectorContainer>
              </div>
            )}
            <div style={{ display: 'flex', gap: 20, width: '100%' }}>
              {selectedPaymentMethodType === 'applePay' ? (
                <ApplePayButton
                  fullWidth
                  onClick={handleApplePayPress}
                  loading={
                    applePayStarting ||
                    applePayProcessing ||
                    waitingForApplePayBalance ||
                    isLoadingGiftFees
                  }
                  disabled={
                    waitingForApplePayBalance ||
                    feeCalculations.totalAmount < 5 ||
                    isLoadingGiftFees
                  }
                />
              ) : (
                <Button
                  fullWidth
                  clickAnimation='bouncy'
                  loading={isLoadingGiftFees || isLoadingGift}
                  onClick={createGiftHandler}
                  disabled={
                    isUserEligible && showFreeGift
                      ? false
                      : !selectedPaymentMethodType
                  }
                >
                  {isUserEligible && showFreeGift
                    ? 'Get Free Gift'
                    : 'Purchase Gift'}
                </Button>
              )}
            </div>
            {feedback && <Message>{feedback}</Message>}
          </FormContainer>
        </ContentRow>
      </DemoContainer>
    </>
  );
};

export default Confirmation;
